/**
 * 連続ブレイクランアウト記録（自前・GPL非依存）。
 *
 * 「ミスなくブレイクランアウト」を達成するたびに連続回数を +1 し、画面隅に
 * 勲章バッジとして表示する。連続回数は localStorage に永続し、リセットや
 * モード変更では消えない（ミス＝的玉0個 or スクラッチ でのみ 0 に戻る）。
 *
 * 判定（ルールエンジン未実装のため現象ベースで定義）:
 *   - 1ショット成功 = 的玉を1個以上ポケット かつ スクラッチなし
 *   - ブレイクランアウト成立 = ラック開始（ブレイク）から最後の的玉まで
 *     全ショット成功で盤面をクリア（runAlive を保ったままクリア到達）
 *   - ミスが出たら runAlive=false かつ連続記録を 0 にリセット
 *
 * DOM（バッジ要素）・localStorage・判定状態をこのモジュールに閉じ込め、
 * main 側はラック開始通知（startRack）とショット結果通知（recordShot）だけ行う。
 */

const STREAK_KEY = 'pool.breakRunStreak';
/** 連続ブレイクランアウトの表示上限（要件: 👑×N、N は最大 9）。
 *  内部カウントもこの上限でクランプする（10 以上溜まり続けると体感が薄れるため）。 */
const MAX_STREAK = 9;

const loadStreak = (): number => {
  try {
    const v = localStorage.getItem(STREAK_KEY);
    if (v === null) return 0;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

const saveStreak = (n: number): void => {
  try {
    localStorage.setItem(STREAK_KEY, String(n));
  } catch (err) {
    console.error('breakRunStreak: localStorage 保存失敗:', err);
  }
};

export interface BreakRunTracker {
  /**
   * 新しいラックの開始（ブレイク前）。進行中ランをリセットする（連続記録は保持）。
   * mineBreak=false の場合は「相手ブレイクのラック」とみなし、このラックでは
   * ブレイクラン成立対象外（runAlive=false で開始）。デフォルト true（ソロ・自分ブレイク）。
   */
  startRack: (mineBreak?: boolean) => void;
  /**
   * 1ショットの結果を反映する。
   *   potted    = このショットでポケットした的玉の数
   *   scratched = 手球が落ちた/盤外（スクラッチ＝ファウル）
   *   cleared   = このショットで盤上の的玉が全て無くなったか
   */
  recordShot: (potted: number, scratched: boolean, cleared: boolean) => void;
  /** 現在の連続ブレイクランアウト数。 */
  count: () => number;
  /** 現在ラックがミスなく進行中か（永続化用）。 */
  isRunAlive: () => boolean;
  /** レジューム復元時に進行中フラグを復旧する（streak は触らない＝startRack の代替）。 */
  resumeRun: (alive: boolean) => void;
  /** ブレイクランアウト成立時に発火するコールバックを差し替える（バナー/Perfect Game 判定用）。 */
  setOnBreakRunout: (cb: (() => void) | null) => void;
}

export const createBreakRunTracker = (): BreakRunTracker => {
  // 永続値も MAX_STREAK で頭打ちにする（過去版で 10 以上が保存されていた場合の正規化）。
  let streak = Math.min(loadStreak(), MAX_STREAK);
  // 現在のラックがブレイクから一度もミスなく進んでいるか。
  let runAlive = false;
  // ブレイクランアウト成立時のコールバック（バナー表示・Perfect Game 判定の通知用）。
  let onBreakRunout: (() => void) | null = null;

  // ---- バッジ DOM（勲章）。CSS は index.html の .pool-badge を参照。 ----
  const badge = document.createElement('div');
  badge.className = 'pool-badge';
  badge.hidden = true;
  const icon = document.createElement('span');
  icon.className = 'pool-badge-icon';
  icon.textContent = '👑';
  const text = document.createElement('span');
  text.className = 'pool-badge-text';
  badge.appendChild(icon);
  badge.appendChild(text);
  document.body.appendChild(badge);

  const render = (pulse: boolean): void => {
    if (streak <= 0) {
      badge.hidden = true;
      return;
    }
    badge.hidden = false;
    text.textContent = `×${streak}`;
    badge.title = `連続ブレイクランアウト ${streak} 回`;
    badge.setAttribute('aria-label', `連続ブレイクランアウト ${streak} 回`);
    if (pulse) {
      // クラスを付け直してアニメーションを再生（reflow を挟んで確実に再発火）。
      badge.classList.remove('is-pulse');
      void badge.offsetWidth;
      badge.classList.add('is-pulse');
    }
  };

  const setStreak = (n: number, pulse: boolean): void => {
    streak = Math.min(MAX_STREAK, Math.max(0, n));
    saveStreak(streak);
    render(pulse);
  };

  // 初期表示（永続値の復元）。
  render(false);

  return {
    startRack: (mineBreak: boolean = true): void => {
      runAlive = mineBreak;
    },
    recordShot: (potted: number, scratched: boolean, cleared: boolean): void => {
      const good = potted >= 1 && !scratched;
      if (!good) {
        // ミス/ファウル: 連続記録が途切れる。
        runAlive = false;
        if (streak > 0) setStreak(0, false);
        return;
      }
      // 成功ショットで盤面クリア かつ ブレイクからミスなし → ブレイクランアウト成立。
      if (cleared && runAlive) {
        setStreak(streak + 1, true);
        // 直後に新ラックが startRack される（runAlive 再初期化）。
        // コールバック発火はカウンタ更新後（呼び出し側は count() で最新値を読める）。
        try {
          onBreakRunout?.();
        } catch (err) {
          console.error('breakRunout コールバック失敗:', err);
        }
      }
    },
    count: (): number => streak,
    isRunAlive: (): boolean => runAlive,
    resumeRun: (alive: boolean): void => {
      runAlive = alive;
    },
    setOnBreakRunout: (cb: (() => void) | null): void => {
      onBreakRunout = cb;
    },
  };
};
